import { formatDateWithTimezone } from '../../utils/formatDateWithTimezone';
/**
 * @file PageList.js
 * @description This page displays a list of portal pages with their respective details such as name, status, population groups, last edited date, publish date and draft status.
 */

import {
    useState,
    useEffect,
    useContext,
    useCallback,
    useMemo,
    useRef,
} from 'react';
import { Link } from 'react-router-dom';

import './PageList.css';
import styles from './PageList.module.scss';
import { getBadgeStatus } from '../helpers/getBadgeStatus';

import {
    createPortalPage,
    queryPortalPages,
    readPortalPage,
} from '../../services/portalPageService';

import { fetchPopulationGroups } from '../../services/populationGroupService';
import { updatePortalPage } from '../../services/portalPageService';
import { deletePortalPage } from '../../services/portalPageService';

import { getDateFromZulu } from '../helpers/getDateFromZulu';
import PaginationComponent from '../helpers/PaginationComponent';

import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';

import { ModalContext, ModalTypes } from '../../store/ModalContext';
import { useNavigate } from 'react-router-dom';

import { getNameForDuplicate } from '../../utils/utils';
import { SecurityAttributes, LogEventType } from '../../utils/data/enums';
import { useAuthContext } from '../../store/AuthContext';
import { logEvent } from '../../services/loggingService';
import { useAdminAudienceEnabled } from '../../hooks/useAdminAudienceEnabled';
import AudienceBadge from '../shared/AudienceBadge';
import EmptyTableRow from '../ui/no_data_indicators/EmptyTableRow';
import { ROUTES } from '../../constants/routes';
import debounce from 'lodash/debounce';
import { useTableSort, SortDirections } from '../../hooks/useTableSort';
import { FilterModal, renderFilterIcon } from '../helpers/FilterComponent';
import { getText } from '../../i18n';
const PageList = () => {
    const navigate = useNavigate();

    function getDraftStatusLabel(page) {
        if (!page._draft) return null;
        if (page._draft.publishDate) return getText('msg_3937952a3a45');
        return getText('msg_298d4c6ac749');
    }

    function getDraftFilterLabel(page) {
        return getDraftStatusLabel(page) || getText('msg_6eef6648406c');
    }

    const { showModal } = useContext(ModalContext);

    const { user, hasAttribute } = useAuthContext();
    const { isAdminAudienceEnabled } = useAdminAudienceEnabled();
    const [viewerAudienceLabel, setViewerAudienceLabel] = useState('');
    const hasViewerDataFilter =
        isAdminAudienceEnabled && Boolean(user?.jwt?.df);
    const showAudienceColumn = isAdminAudienceEnabled && !hasViewerDataFilter;

    //Data Manipulation Data Prep
    const defaultPageSize = 20;
    const [sideloadedGroupsByID, setSideloadedGroupsByID] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [sortedPages, setSortedPages] = useState([]);
    const [rowsClicked, setRowsClicked] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(defaultPageSize);
    const [paginatedPages, setPaginatedPages] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeModal, setActiveModal] = useState(null);
    const [selections, setSelections] = useState({});

    const { sortConfig, resetSortConfig, renderSortArrow } = useTableSort(
        'name',
        SortDirections.ASCENDING,
    );

    const selectAllRef = useRef(null);

    const handleSelectAllChange = event => {
        setRowsClicked(
            event.target.checked ? sortedForDisplay.map(page => page.id) : [],
        );
    };

    const handlePageChange = newPage => {
        setCurrentPage(newPage);
    };

    const getStatusLabel = page =>
        getBadgeStatus().PortalStatusString(page.status);

    const getPublishDateLabel = page =>
        page.publishDate ? getDateFromZulu(page.publishDate) : '-';

    const getVisibleLabel = page =>
        page.status === 1
            ? getText('msg_1fe59390acc1')
            : getText('msg_d4c2792a7245');

    const getPublishDateSortValue = page => {
        if (!page.publishDate) {
            return null;
        }

        const timestamp = new Date(page.publishDate).getTime();
        return Number.isNaN(timestamp) ? null : timestamp;
    };

    const getPopulationGroupNames = page =>
        (page.populationGroups || [])
            .map(groupId => sideloadedGroupsByID?.[groupId]?.name)
            .filter(Boolean);

    const filteredPages = useMemo(() => {
        if (Object.keys(selections).length === 0) {
            return sortedPages;
        }

        return sortedPages.filter(page => {
            return Object.entries(selections).every(
                ([column, selectedValues]) => {
                    if (!selectedValues || selectedValues.length === 0) {
                        return true;
                    }

                    if (column === 'status') {
                        return selectedValues.includes(getStatusLabel(page));
                    }

                    if (column === 'populationGroups') {
                        const groupNames = getPopulationGroupNames(page);
                        return selectedValues.some(value =>
                            groupNames.includes(value),
                        );
                    }

                    if (column === 'publishDate') {
                        return selectedValues.includes(
                            getPublishDateLabel(page),
                        );
                    }

                    if (column === 'draft') {
                        return selectedValues.includes(
                            getDraftFilterLabel(page),
                        );
                    }

                    if (column === 'visible') {
                        return selectedValues.includes(getVisibleLabel(page));
                    }

                    return true;
                },
            );
        });
    }, [sortedPages, selections, sideloadedGroupsByID]);

    const sortedForDisplay = useMemo(() => {
        if (!sortConfig.key) return filteredPages;

        return [...filteredPages].sort((a, b) => {
            const key = sortConfig.key;

            if (key === 'publishDate') {
                const aVal = getPublishDateSortValue(a);
                const bVal = getPublishDateSortValue(b);

                if (aVal === null && bVal === null) {
                    return 0;
                }

                if (aVal === null) {
                    return 1;
                }

                if (bVal === null) {
                    return -1;
                }

                return sortConfig.direction === SortDirections.ASCENDING
                    ? aVal - bVal
                    : bVal - aVal;
            }

            const aVal = a?.[key];
            const bVal = b?.[key];

            // Case-insensitive string sort for "name" (or any string field)
            if (typeof aVal === 'string' && typeof bVal === 'string') {
                const cmp = aVal.localeCompare(bVal, undefined, {
                    sensitivity: 'base',
                });
                return sortConfig.direction === SortDirections.ASCENDING
                    ? cmp
                    : -cmp;
            }

            // Fallback for numbers/dates/etc.
            if (aVal < bVal)
                return sortConfig.direction === SortDirections.ASCENDING
                    ? -1
                    : 1;
            if (aVal > bVal)
                return sortConfig.direction === SortDirections.ASCENDING
                    ? 1
                    : -1;
            return 0;
        });
    }, [filteredPages, sortConfig]);

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate =
                rowsClicked.length > 0 &&
                rowsClicked.length < sortedForDisplay.length;
        }
    }, [rowsClicked, sortedForDisplay]);

    useEffect(() => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        setPaginatedPages(sortedForDisplay.slice(startIndex, endIndex));
    }, [currentPage, sortedForDisplay, pageSize]);

    useEffect(() => {
        setTotalPages(Math.ceil(sortedForDisplay.length / pageSize));
        setCurrentPage(1);
    }, [sortedForDisplay, pageSize]);

    const handlerRowClicked = id => {
        setRowsClicked(prev => {
            if (prev.includes(id)) {
                return prev.filter(item => item !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    const fetchInitialData = useCallback(
        async (term = '') => {
            setIsLoading(true);
            try {
                const [pagesResponse, populationGroupsResponse] =
                    await Promise.all([
                        queryPortalPages(true, null, false, term, true),
                        fetchPopulationGroups(),
                    ]);

                const allPages = pagesResponse.viewModels;
                const draftByLiveId = allPages.reduce((acc, p) => {
                    if (p.draftOfPortalId) acc[p.draftOfPortalId] = p;
                    return acc;
                }, {});
                const livePages = allPages
                    .filter(p => !p.draftOfPortalId)
                    .map(p =>
                        draftByLiveId[p.id]
                            ? { ...p, _draft: draftByLiveId[p.id] }
                            : p,
                    );
                setSortedPages(livePages);
                setPaginatedPages(livePages.slice(0, pageSize));

                const groupsByID = populationGroupsResponse.reduce(
                    (acc, group) => {
                        acc[group.id] = group;
                        return acc;
                    },
                    {},
                );

                const defaultGroup = populationGroupsResponse.find(
                    group => group.id === user?.jwt?.df,
                );

                setSideloadedGroupsByID(groupsByID);
                setViewerAudienceLabel(
                    defaultGroup?.name || String(user?.jwt?.df || ''),
                );
                setTotalPages(Math.ceil(livePages.length / pageSize));
            } catch (error) {
                console.error('Error fetching data:', error);
                Toast.error(getText('msg_903c20071112'));
            } finally {
                setIsLoading(false);
            }
        },
        [pageSize, user?.jwt?.df],
    );

    const debouncedFetch = useMemo(
        () => debounce(term => fetchInitialData(term), 500),
        [fetchInitialData],
    );

    useEffect(() => {
        return () => {
            debouncedFetch.cancel();
        };
    }, [debouncedFetch]);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    const handleSearchChange = e => {
        const { value } = e.target;
        setSearchTerm(value);
        debouncedFetch(value);
    };

    const openModal = column => {
        setActiveModal(column);
    };

    const closeModal = () => {
        setActiveModal(null);
    };

    const handleApply = (column, selectedOptions) => {
        setSelections(prev => ({ ...prev, [column]: selectedOptions }));
    };

    const clearFilters = () => {
        debouncedFetch.cancel();
        resetSortConfig();
        setSelections({});
        setSearchTerm('');
        setRowsClicked([]);
        closeModal();
        fetchInitialData('');
    };

    const getOptions = column => {
        if (column === 'status') {
            return Array.from(new Set(sortedPages.map(getStatusLabel))).sort(
                (a, b) => a.localeCompare(b),
            );
        }

        if (column === 'populationGroups') {
            return Array.from(
                new Set(
                    sortedPages.flatMap(page => getPopulationGroupNames(page)),
                ),
            ).sort((a, b) => a.localeCompare(b));
        }

        if (column === 'publishDate') {
            return Array.from(
                new Set(sortedPages.map(getPublishDateLabel)),
            ).sort((a, b) => a.localeCompare(b));
        }

        if (column === 'draft') {
            return Array.from(
                new Set(sortedPages.map(getDraftFilterLabel)),
            ).sort((a, b) => a.localeCompare(b));
        }

        if (column === 'visible') {
            return Array.from(new Set(sortedPages.map(getVisibleLabel))).sort(
                (a, b) => a.localeCompare(b),
            );
        }

        return [];
    };

    const onToggle = id => {
        sortedPages.filter(page => {
            if (page.id === id) {
                // If trying to disable and a draft exists, block and show warning
                if (page.status === 1 && page._draft) {
                    Toast.warning(
                        getText('msg_06bd19c84b11'),
                        {},
                        getText('msg_aaa9e5c95265', { pageName: page.name }),
                    );
                    return page;
                }
                if (page.status === 1) {
                    page.status = 2;
                } else {
                    page.status = 1;
                }

                let updatedPage = {
                    id: page.id,
                    status: page.status,
                };

                updatePortalPage(updatedPage).then(response => {});
            }
            return page;
        });
        setSortedPages([...sortedPages]);
    };

    const deletePages = async () => {
        const selectedPages = sortedPages.filter(page =>
            rowsClicked.includes(page.id),
        );

        if (selectedPages.length === 0) {
            return;
        }

        let deletedCount = 0;

        for (const page of selectedPages) {
            const { id, name, status } = page;

            // If status is "Enabled", dont' allow deletion
            if (status === 1) {
                Toast.warning(
                    getText('msg_eb6559d8f541'),
                    {},
                    getText('msg_4f2d47cee327', { pageName: name }),
                );
                continue;
            }

            // If status is not "Enabled", proceed to deletion
            try {
                await deletePortalPage(id);
                deletedCount += 1;
                Toast.success(getText('msg_c0a3cc301da7', { pageName: name }));
            } catch (error) {
                console.error(`Error deleting portal '${name}':`, error);
                Toast.error(getText('msg_0f7ef8fc636c', { pageName: name }));
            }
        }

        if (deletedCount > 0) {
            await fetchInitialData(searchTerm);
        }

        setRowsClicked([]);
    };

    const duplicatePages = async () => {
        const newSortedPages = [...sortedPages];

        for (const rowId of rowsClicked) {
            const originalPage = sortedPages.find(p => p.id === rowId);
            if (originalPage) {
                try {
                    const duplicatedPage = { ...originalPage };
                    delete duplicatedPage.id;
                    delete duplicatedPage.publishDate;
                    delete duplicatedPage.order;
                    duplicatedPage.status = 3;
                    duplicatedPage.parentId = null;

                    const existingNames = sortedPages.map(page => page.name);
                    const newName = getNameForDuplicate(
                        duplicatedPage.name,
                        existingNames,
                    );
                    duplicatedPage.name = newName;

                    const fetchedPage = await readPortalPage(
                        originalPage.id,
                        true,
                    );
                    const containers =
                        fetchedPage.viewModels[0]?.containers || [];
                    const payload = { ...duplicatedPage, containers };

                    delete payload.createdAt;
                    delete payload.createdBy;
                    delete payload.updatedAt;
                    delete payload.updatedBy;

                    const createdPortal = await createPortalPage(payload);
                    duplicatedPage.id = createdPortal.viewModels[0].id;

                    newSortedPages.push(duplicatedPage);
                } catch (error) {
                    console.error('Error duplicating page:', error);
                    Toast.error(getText('msg_18c5d22c3797'));
                }
            }
        }

        setSortedPages(newSortedPages);

        setRowsClicked([]);

        const newTotalPages = Math.ceil(newSortedPages.length / pageSize);
        setTotalPages(newTotalPages);
    };

    const rowAction = action => {
        if (rowsClicked.length > 0) {
            switch (action) {
                case 'delete':
                    showModal({
                        type: ModalTypes.DELETE_CONFIRMATION,
                        title: getText('msg_b957efeb6067'),
                        subtitle: getText('msg_604f4c5d6a2c'),
                        onYes: () => deletePages(),
                        onNo: () => {},
                        noButtonText: getText('msg_77dfd2135f4d'),
                        yesButtonText: getText('msg_f6fdbe48dc54'),
                    });
                    logEvent(
                        LogEventType.DeleteClicked,
                        `Portal pages delete clicked`,
                    );
                    break;
                case 'duplicate':
                    duplicatePages();
                    break;
                default:
                    break;
            }
        } else {
            Toast.error(getText('msg_9bcf67dc54d5'));
        }
    };

    return (
        <div className="page-content">
            <div className="card">
                <div className="card-body px-0 py-4">
                    {/* Page Header with Title and Action */}
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        {/* Page Title */}
                        <div
                            style={{ display: 'flex', flexDirection: 'column' }}
                        >
                            <p className="page-title float-start card-title ms-4">
                                {getText('msg_a0c88242e813')}
                            </p>
                            <p
                                className="ing-portals-subtitle mt-1"
                                style={{ marginTop: '10px' }}
                            >
                                {getText('msg_42110e6b407f')}
                            </p>
                        </div>

                        {/* Page Actions */}
                        {(hasViewerDataFilter ||
                            !hasAttribute(
                                SecurityAttributes.PortalsReadOnly,
                            )) && (
                            <div className="float-end d-flex align-items-center">
                                {hasViewerDataFilter && (
                                    <span className="mx-3">
                                        <AudienceBadge
                                            label={viewerAudienceLabel}
                                        />
                                    </span>
                                )}
                                {!hasAttribute(
                                    SecurityAttributes.PortalsReadOnly,
                                ) && (
                                    <>
                                        <span
                                            type="button"
                                            onClick={clearFilters}
                                            className="ing-text-buttons mx-3"
                                        >
                                            <i className="bi bi-arrow-counterclockwise me-2" />
                                            {getText('msg_381c4bf1d409')}
                                        </span>
                                        <span
                                            type="button"
                                            onClick={() => rowAction('delete')}
                                            className="ing-text-buttons mx-3"
                                        >
                                            <i className="bi bi-trash3 me-2" />
                                            {getText('msg_f6fdbe48dc54')}
                                        </span>
                                        <span
                                            type="button"
                                            onClick={() =>
                                                rowAction('duplicate')
                                            }
                                            className="ing-text-buttons mx-3"
                                        >
                                            <i className="bi bi-copy me-2" />
                                            {getText('msg_972d57379db3')}
                                        </span>
                                        <span
                                            onClick={() =>
                                                navigate(`new-portal-page`)
                                            }
                                            type="button"
                                            className="ms-3 me-4  button-base ing-button-primary-left "
                                        >
                                            <i className="bi bi-plus-lg me-1" />
                                            {getText('msg_61cc55aa0453')}
                                        </span>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="d-flex align-items-center px-4 pb-4 gap-3">
                        <div className="form-group col-12 col-lg-6 position-relative mb-0">
                            <input
                                id="portalSearch"
                                type="text"
                                maxLength="100"
                                className="form-control pe-5"
                                value={searchTerm}
                                onChange={handleSearchChange}
                                name="portalSearch"
                                placeholder={getText('msg_615fa465c753')}
                            />
                            <i
                                className="bi bi-search position-absolute"
                                style={{
                                    right: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#667085',
                                    pointerEvents: 'none',
                                }}
                            />
                        </div>
                        <div className="ms-auto">
                            <Link
                                to={ROUTES.PORTAL_PAGES_ARRANGE_TABS}
                                className="linkToArrangeTabs"
                            >
                                {getText('msg_b9315082e7c3')}
                            </Link>
                        </div>
                    </div>
                    <div style={{ minHeight: '70vh' }}>
                        {isLoading ? (
                            <Spinner
                                width={50}
                                className="pageListloadingSpinner"
                            />
                        ) : (
                            <>
                                <div className="table-responsive-xxl w-100">
                                        {/* Page List Table */}
                                        <table className="table table-hover align-middle">
                                            <colgroup>
                                                <col className={styles.colSelect} />
                                                <col className={styles.colName} />
                                                <col
                                                    className={
                                                        styles.colDisplayName
                                                    }
                                                />
                                                {showAudienceColumn && (
                                                    <col
                                                        className={
                                                            styles.colAudience
                                                        }
                                                    />
                                                )}
                                                <col
                                                    className={
                                                        styles.colPopulationGroups
                                                    }
                                                />
                                                <col
                                                    className={
                                                        styles.colMainPortal
                                                    }
                                                />
                                                <col className={styles.colStatus} />
                                                <col
                                                    className={
                                                        styles.colDraftStatus
                                                    }
                                                />
                                                <col
                                                    className={
                                                        styles.colPublishDate
                                                    }
                                                />
                                                <col className={styles.colActive} />
                                            </colgroup>
                                            <thead className="custom-table-header table-light">
                                                <tr>
                                                    <th
                                                        className="text-center checkbox-column-head"
                                                        style={{
                                                            paddingLeft: '20px',
                                                        }}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            ref={selectAllRef}
                                                            checked={
                                                                rowsClicked.length ===
                                                                    sortedForDisplay.length &&
                                                                sortedForDisplay.length >
                                                                    0
                                                            }
                                                            onChange={
                                                                handleSelectAllChange
                                                            }
                                                        />
                                                    </th>
                                                    <th>
                                                        <span className="table-column-header-text">
                                                            {getText(
                                                                'msg_709a23220f2c',
                                                            )}
                                                        </span>
                                                        {renderSortArrow(
                                                            'name',
                                                        )}
                                                    </th>
                                                    <th>
                                                        <span className="table-column-header-text">
                                                            {getText(
                                                                'msg_8d6b34815ba4',
                                                            )}
                                                        </span>
                                                        {renderSortArrow(
                                                            'displayName',
                                                        )}
                                                    </th>
                                                    {showAudienceColumn && (
                                                        <th>
                                                            <span className="table-column-header-text">
                                                                {getText(
                                                                    'msg_51d9934555e2',
                                                                )}
                                                            </span>
                                                        </th>
                                                    )}
                                                    <th>
                                                        <span className="table-column-header-text">
                                                            {getText(
                                                                'msg_e744dd6b3c48',
                                                            )}
                                                        </span>
                                                        <span
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                openModal(
                                                                    'populationGroups',
                                                                );
                                                            }}
                                                            className="ms-2"
                                                        >
                                                            {renderFilterIcon(
                                                                selections[
                                                                    'populationGroups'
                                                                ]?.length > 0,
                                                            )}
                                                        </span>
                                                        {activeModal ===
                                                            'populationGroups' && (
                                                            <FilterModal
                                                                column={
                                                                    activeModal
                                                                }
                                                                options={getOptions(
                                                                    activeModal,
                                                                )}
                                                                selectedOptions={
                                                                    selections[
                                                                        activeModal
                                                                    ] || []
                                                                }
                                                                onApply={
                                                                    handleApply
                                                                }
                                                                onClose={
                                                                    closeModal
                                                                }
                                                            />
                                                        )}
                                                    </th>
                                                    <th className="text-center">
                                                        <span className="table-column-header-text">
                                                            {getText(
                                                                'msg_eb0ea3dcaee1',
                                                            )}
                                                        </span>
                                                    </th>
                                                    <th>
                                                        <span className="table-column-header-text">
                                                            {getText(
                                                                'msg_bae7d5be7082',
                                                            )}
                                                        </span>
                                                        {renderSortArrow(
                                                            'status',
                                                        )}
                                                        <span
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                openModal(
                                                                    'status',
                                                                );
                                                            }}
                                                            className="ms-2"
                                                        >
                                                            {renderFilterIcon(
                                                                selections[
                                                                    'status'
                                                                ]?.length > 0,
                                                            )}
                                                        </span>
                                                        {activeModal ===
                                                            'status' && (
                                                            <FilterModal
                                                                column={
                                                                    activeModal
                                                                }
                                                                options={getOptions(
                                                                    activeModal,
                                                                )}
                                                                selectedOptions={
                                                                    selections[
                                                                        activeModal
                                                                    ] || []
                                                                }
                                                                onApply={
                                                                    handleApply
                                                                }
                                                                onClose={
                                                                    closeModal
                                                                }
                                                            />
                                                        )}
                                                    </th>
                                                    <th
                                                        className="text-start position-relative"
                                                        style={{
                                                            minWidth: '120px',
                                                        }}
                                                    >
                                                        <span className="table-column-header-text">
                                                            {getText(
                                                                'msg_23d33e22acfc',
                                                            )}
                                                        </span>
                                                        <span
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                openModal(
                                                                    'draft',
                                                                );
                                                            }}
                                                            className="ms-2"
                                                        >
                                                            {renderFilterIcon(
                                                                selections[
                                                                    'draft'
                                                                ]?.length > 0,
                                                            )}
                                                        </span>
                                                        {activeModal ===
                                                            'draft' && (
                                                            <FilterModal
                                                                column={
                                                                    activeModal
                                                                }
                                                                options={getOptions(
                                                                    activeModal,
                                                                )}
                                                                selectedOptions={
                                                                    selections[
                                                                        activeModal
                                                                    ] || []
                                                                }
                                                                onApply={
                                                                    handleApply
                                                                }
                                                                onClose={
                                                                    closeModal
                                                                }
                                                            />
                                                        )}
                                                    </th>
                                                    <th
                                                        style={{
                                                            whiteSpace:
                                                                'nowrap',
                                                            width: '1%',
                                                        }}
                                                    >
                                                        <span className="table-column-header-text">
                                                            {getText(
                                                                'msg_39a878eb854d',
                                                            )}
                                                        </span>
                                                        {renderSortArrow(
                                                            'publishDate',
                                                        )}
                                                        <span
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                openModal(
                                                                    'publishDate',
                                                                );
                                                            }}
                                                            className="ms-2"
                                                        >
                                                            {renderFilterIcon(
                                                                selections[
                                                                    'publishDate'
                                                                ]?.length > 0,
                                                            )}
                                                        </span>
                                                        {activeModal ===
                                                            'publishDate' && (
                                                            <FilterModal
                                                                column={
                                                                    activeModal
                                                                }
                                                                options={getOptions(
                                                                    activeModal,
                                                                )}
                                                                selectedOptions={
                                                                    selections[
                                                                        activeModal
                                                                    ] || []
                                                                }
                                                                onApply={
                                                                    handleApply
                                                                }
                                                                onClose={
                                                                    closeModal
                                                                }
                                                            />
                                                        )}
                                                    </th>
                                                    <th
                                                        className="text-center position-relative"
                                                        style={{
                                                            paddingRight:
                                                                '20px',
                                                        }}
                                                    >
                                                        <span className="table-column-header-text">
                                                            {getText(
                                                                'msg_1fe59390acc1',
                                                            )}
                                                        </span>
                                                        <span
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                openModal(
                                                                    'visible',
                                                                );
                                                            }}
                                                            className="ms-2"
                                                        >
                                                            {renderFilterIcon(
                                                                selections[
                                                                    'visible'
                                                                ]?.length > 0,
                                                            )}
                                                        </span>
                                                        {activeModal ===
                                                            'visible' && (
                                                            <FilterModal
                                                                column={
                                                                    activeModal
                                                                }
                                                                isRightColumn={
                                                                    true
                                                                }
                                                                options={getOptions(
                                                                    activeModal,
                                                                )}
                                                                selectedOptions={
                                                                    selections[
                                                                        activeModal
                                                                    ] || []
                                                                }
                                                                onApply={
                                                                    handleApply
                                                                }
                                                                onClose={
                                                                    closeModal
                                                                }
                                                            />
                                                        )}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sortedPages.length === 0 && (
                                                    <EmptyTableRow
                                                        colSpan={
                                                            showAudienceColumn
                                                                ? 10
                                                                : 9
                                                        }
                                                        title={
                                                            searchTerm
                                                                ? 'Sorry, no results found'
                                                                : 'Sorry, there are no portals to display'
                                                        }
                                                        description={
                                                            searchTerm
                                                                ? 'Try adjusting your search to find what you are looking for'
                                                                : 'You can add a new portal by clicking "Add" on the top right corner'
                                                        }
                                                    />
                                                )}
                                                {paginatedPages.map(
                                                    (page, index) => (
                                                        <tr
                                                            key={page.id}
                                                            className={`${rowsClicked.includes(page.id) ? 'clicked-class' : ''}`}
                                                        >
                                                            <td
                                                                className="text-center"
                                                                style={{
                                                                    paddingLeft:
                                                                        '20px',
                                                                }}
                                                            >
                                                                <input
                                                                    onClick={() =>
                                                                        handlerRowClicked(
                                                                            page.id,
                                                                        )
                                                                    }
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    checked={rowsClicked.includes(
                                                                        page.id,
                                                                    )}
                                                                />
                                                            </td>
                                                            <td
                                                                className="item-page-name titleCell"
                                                                onClick={() =>
                                                                    navigate(
                                                                        'new-portal-page',
                                                                        {
                                                                            state: {
                                                                                id: page._draft
                                                                                    ? page
                                                                                          ._draft
                                                                                          .id
                                                                                    : page.id,
                                                                            },
                                                                        },
                                                                    )
                                                                }
                                                                role="button"
                                                                tabIndex={0}
                                                                onKeyDown={e => {
                                                                    if (
                                                                        e.key ===
                                                                            'Enter' ||
                                                                        e.key ===
                                                                            ' '
                                                                    ) {
                                                                        navigate(
                                                                            'new-portal-page',
                                                                            {
                                                                                state: {
                                                                                    id: page._draft
                                                                                        ? page
                                                                                              ._draft
                                                                                              .id
                                                                                        : page.id,
                                                                                },
                                                                            },
                                                                        );
                                                                    }
                                                                }}
                                                            >
                                                                {page.name}
                                                            </td>
                                                            <td className="item-page-name subtitleCell">
                                                                {
                                                                    page.displayName
                                                                }
                                                            </td>
                                                            {showAudienceColumn && (
                                                                <td>
                                                                    {page.dataFilter ? (
                                                                        <AudienceBadge
                                                                            label={
                                                                                page.dataFilter
                                                                            }
                                                                            showIcon={
                                                                                false
                                                                            }
                                                                        />
                                                                    ) : (
                                                                        '-'
                                                                    )}
                                                                </td>
                                                            )}
                                                            <td>
                                                                <div
                                                                    style={{
                                                                        display:
                                                                            'flex',
                                                                        flexWrap:
                                                                            'wrap',
                                                                        flexDirection:
                                                                            'row',
                                                                        alignItems:
                                                                            'flex-start',
                                                                        gap: '6px',
                                                                        maxWidth:
                                                                            '300px',
                                                                    }}
                                                                >
                                                                    {page.populationGroups.map(
                                                                        (
                                                                            groupID,
                                                                            index,
                                                                        ) => (
                                                                            <span className="badge-base badge-population-group">
                                                                                {sideloadedGroupsByID !==
                                                                                null
                                                                                    ? sideloadedGroupsByID[
                                                                                          groupID
                                                                                      ]
                                                                                          .name
                                                                                    : ''}
                                                                            </span>
                                                                        ),
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="subtitleCell text-center">
                                                                {!page.isChild
                                                                    ? 'Y'
                                                                    : '-'}
                                                            </td>
                                                            <td>
                                                                <span
                                                                    className={`item-page-status badge-base ${getBadgeStatus().Class(page.status)}`}
                                                                >
                                                                    <svg
                                                                        className="me-1"
                                                                        width={
                                                                            8
                                                                        }
                                                                        height={
                                                                            8
                                                                        }
                                                                        viewBox="0 0 8 8"
                                                                        fill="none"
                                                                    >
                                                                        <circle
                                                                            cx={
                                                                                4
                                                                            }
                                                                            cy={
                                                                                4
                                                                            }
                                                                            r={
                                                                                3
                                                                            }
                                                                            fill={getBadgeStatus().SVGColor(
                                                                                page.status,
                                                                            )}
                                                                        />
                                                                    </svg>
                                                                    {getBadgeStatus().PortalStatusString(
                                                                        page.status,
                                                                    )}
                                                                </span>
                                                            </td>
                                                            <td
                                                                className="text-start"
                                                                style={{
                                                                    minWidth:
                                                                        '120px',
                                                                }}
                                                            >
                                                                {(() => {
                                                                    const label =
                                                                        getDraftStatusLabel(
                                                                            page,
                                                                        );
                                                                    if (!label)
                                                                        return null;
                                                                    if (
                                                                        label ===
                                                                            'Draft Scheduled' &&
                                                                        page._draft &&
                                                                        page
                                                                            ._draft
                                                                            .publishDate
                                                                    ) {
                                                                        return (
                                                                            <span
                                                                                className="badge-base badge-status-inactive"
                                                                                title={`Scheduled for: ${formatDateWithTimezone(page._draft.publishDate)}`}
                                                                            >
                                                                                {
                                                                                    label
                                                                                }
                                                                            </span>
                                                                        );
                                                                    }
                                                                    return (
                                                                        <span className="badge-base badge-status-inactive">
                                                                            {
                                                                                label
                                                                            }
                                                                        </span>
                                                                    );
                                                                })()}
                                                            </td>
                                                            <td
                                                                className="subtitleCell"
                                                                style={{
                                                                    fontVariantNumeric:
                                                                        'tabular-nums',
                                                                    whiteSpace:
                                                                        'nowrap',
                                                                    width: '1%',
                                                                }}
                                                            >
                                                                {page.publishDate
                                                                    ? getDateFromZulu(
                                                                          page.publishDate,
                                                                      )
                                                                    : '-'}
                                                            </td>
                                                            <td
                                                                className="text-center"
                                                                style={{
                                                                    paddingRight:
                                                                        '20px',
                                                                }}
                                                            >
                                                                <div
                                                                    className={`form-switch ${page.status !== 1 && page.status !== 2 ? 'form-switch-locked' : ''}`}
                                                                >
                                                                    <input
                                                                        onClick={() =>
                                                                            onToggle(
                                                                                page.id,
                                                                            )
                                                                        }
                                                                        className="form-check-input custom-switch-input"
                                                                        type="checkbox"
                                                                        checked={
                                                                            page.status ===
                                                                            1
                                                                                ? true
                                                                                : false
                                                                        }
                                                                        disabled={
                                                                            (page.status !==
                                                                                1 &&
                                                                                page.status !==
                                                                                    2) ||
                                                                            hasAttribute(
                                                                                SecurityAttributes.PortalsReadOnly,
                                                                            )
                                                                                ? true
                                                                                : false
                                                                        }
                                                                    />
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ),
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                {totalPages > 0 && (
                                    <div className="mt-auto">
                                        <PaginationComponent
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            onPageChange={handlePageChange}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PageList;
